import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X, CheckCircle2, Loader2, Copy, Info, Calculator, ShieldCheck } from 'lucide-react';
import axios from 'axios';

/**
 * CTC Group Editor — isolated component so the big BusinessControls page
 * doesn't re-render on every keystroke and cause the salary-head dropdowns
 * to "jump" or close.
 *
 * Calc types per salary head row
 * ------------------------------
 *   fixed        → value (₹/year)
 *   percentage   → pct_val % of <pct_of>  (pct_of = any salary head CODE, or
 *                                          one of the constants: CTC, GROSS,
 *                                          BASIC_DA — typed freely)
 *   balancing    → auto = CTC − sum(other CTC components, annualised)
 *                  Only one row may be `balancing` per group.
 *
 * ctc_component flag
 * ------------------
 *   true  → part of the CTC package (factors into total & balancing)
 *   false → "Above-CTC" item (bonus, reimbursement, insurance premium paid
 *           by company) — shown on payslip but not in CTC total.
 */

const CTC_REF_CONSTANTS = [
  { value: 'CTC',      label: 'CTC (annual cost to company)' },
  { value: 'BASIC',    label: 'BASIC head' },
  { value: 'BASIC_DA', label: 'BASIC + DA' },
  { value: 'GROSS',    label: 'GROSS (sum of all earnings)' },
];

// ----- Statutory head detection -----
// Standard payroll head codes for PF / ESI across employee + employer sides.
// Used by the live-preview engine to apply the wage ceilings (₹15K for PF,
// ₹21K for ESI) and ESI eligibility cut-off. Keeping it data-driven via
// startsWith catches custom variants (PF_E, PF_EMP, PF_EMPLOYEE, EPF_E, …).
const isPfHeadCode = (code) => {
  const c = (code || '').toUpperCase();
  if (!c) return false;
  return c === 'PF' || c.startsWith('PF_') || c.startsWith('EPF_');
};
const isEsiHeadCode = (code) => {
  const c = (code || '').toUpperCase();
  if (!c) return false;
  return c === 'ESI' || c.startsWith('ESI_');
};
// Employer-side detection — adds to CTC L1 but NOT to gross. Heuristic looks
// for "_ER", "_EMPLOYER" suffixes anywhere in the code. Master's head_type
// ('employer_contribution') is checked first in the engine itself; this is
// just a fallback when the master lacks the explicit type.
const isEmployerSideHead = (code) => {
  const c = (code || '').toUpperCase();
  if (!c) return false;
  return c.endsWith('_ER') || c.endsWith('_EMPLOYER') || c.includes('_ER_') || c.includes('_EMPLOYER_') || c === 'EPS';
};

export default function CTCGroupEditor({
  saving,
  onCancel,
  onCreate,
  salaryHeadMaster = [],
  statutoryConfig = null,   // { pf: {wage_ceiling, employee_rate, employer_rate, is_active}, esi: {...} }
  initial = null,
}) {
  const [clientCompanies, setClientCompanies] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${backendUrl}/api/companies`, { withCredentials: true })
      .then(res => {
        if (isMounted && res.data) {
          const list = Array.isArray(res.data) ? res.data : (res.data.companies || res.data.data || []);
          setClientCompanies(list);
        }
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);

  // Local draft — isolated from parent. Only persisted on Submit.
  const [draft, setDraft] = useState(() => {
    const initSB = initial?.statutory_benefits || {};
    return {
      name: '',
      code: '',
      description: '',
      parent_group: '',         // top-level: e.g. "Staff" / "O&M"
      sub_group: '',            // optional finer split: e.g. "Plumbing" / "Electrical"
      // Pay cycle (cutoff dates per group)
      pay_cycle_start_day: '',
      pay_cycle_end_day: '',
      pay_cycle_label: '',
      effective_from: new Date().toISOString().slice(0, 10),
      applicable_grades: [],
      applicable_categories: [],
      salary_heads: [],
      include_food: false, include_gratuity: false, include_telephone: false,
      include_mobile: false, include_internet: false, include_conveyance: false,
      include_driver: false, include_books: false, include_meal_card: false,
      include_health_insurance: false, include_lta: false,
      custom_perks: [],
      // ---- Annual anchors ----
      ctc_amount: 600000,
      gross_amount: 300000,
      // ----- Base Days config -----
      base_days_method: 'fixed',   // 'fixed' | 'calendar' | 'working_days'
      base_days_fixed: 30,         // used when method='fixed' (e.g. 26 / 30 / custom)
      // ----- OT calc config -----
      ot_enabled: false,
      ot_numerator: 'BASIC',       // 'BASIC' | 'CTC_MINUS_HRA' | 'GROSS' | 'CUSTOM'
      ot_numerator_custom: '',     // when 'CUSTOM' — head code or fixed₹
      ot_numerator_factor: 1.0,    // optional multiplier on numerator (e.g. 0.88 for "(CTC-HRA)*0.88")
      ot_divisor: 'BASE_DAYS',     // 'BASE_DAYS' | '240' | 'CALENDAR_DAYS' | 'CUSTOM'
      ot_divisor_custom: 0,        // used when 'CUSTOM'
      ot_multiplier: 2.0,          // 1, 1.5, 2, custom
      ot_demo_days: 2,             // UI-only: live preview "what if 2 OT days"
      // ----- F&F Salary Proration Method -----
      salary_proration_method: 'WAGE_DAYS', // 'BASE_DAYS' | 'WAGE_DAYS'
      ...(initial || {}),
      statutory_benefits: {
        gratuity: {
          is_eligible: false,
          frequency: 'monthly',
          amount_mode: 'auto_calculate',
          eligibility_period: null,
          calculation_period: null,
          period_lock: false,
          payment_responsibility: 'COMPANY_PAYS',
          client_company_id: null,
          effective_from: null,
          effective_to: null,
          ...(initSB.gratuity || {}),
        },
        bonus: {
          is_eligible: false,
          frequency: 'annual',
          amount_mode: 'auto_calculate',
          eligibility_period: null,
          calculation_period: null,
          period_lock: false,
          payment_responsibility: 'COMPANY_PAYS',
          client_company_id: null,
          effective_from: null,
          effective_to: null,
          ...(initSB.bonus || {}),
        },
        leave_encashment: {
          is_eligible: false,
          frequency: 'annual',
          amount_mode: 'auto_calculate',
          eligibility_period: null,
          calculation_period: null,
          period_lock: false,
          payment_responsibility: 'COMPANY_PAYS',
          client_company_id: null,
          effective_from: null,
          effective_to: null,
          ...(initSB.leave_encashment || {}),
        },
      },
    };
  });

  const set = useCallback((patch) => setDraft(prev => ({ ...prev, ...patch })), []);

  const setStatutoryBenefit = useCallback((bKey, patch) => {
    setDraft(prev => {
      const currentSB = prev.statutory_benefits || {};
      const currentCfg = currentSB[bKey] || {};
      let updatedCfg = { ...currentCfg, ...patch };
      if (patch.is_eligible === false) {
        updatedCfg = {
          is_eligible: false,
          frequency: bKey === 'gratuity' ? 'monthly' : 'annual',
          amount_mode: 'auto_calculate',
          eligibility_period: null,
          calculation_period: null,
          period_lock: false,
          payment_responsibility: 'COMPANY_PAYS',
          client_company_id: null,
          effective_from: null,
          effective_to: null,
        };
      } else if (patch.payment_responsibility === 'COMPANY_PAYS') {
        updatedCfg.client_company_id = null;
      }
      return {
        ...prev,
        statutory_benefits: {
          ...currentSB,
          [bKey]: updatedCfg,
        },
      };
    });
  }, []);

  const setHead = useCallback((idx, patch) => {
    setDraft(prev => {
      const heads = prev.salary_heads.map((h, i) => i === idx ? { ...h, ...patch } : h);
      return { ...prev, salary_heads: heads };
    });
  }, []);

  const addHead = useCallback(() => {
    setDraft(prev => ({
      ...prev,
      salary_heads: [...prev.salary_heads, {
        code: '', name: '', type: 'earning', calc: 'fixed',
        pct_of: 'CTC', pct_val: '', value: '',
        ctc_component: true,
      }],
    }));
  }, []);

  const removeHead = useCallback((idx) => {
    setDraft(prev => ({
      ...prev,
      salary_heads: prev.salary_heads.filter((_, i) => i !== idx),
    }));
  }, []);

  const setPerk = useCallback((idx, patch) => {
    setDraft(prev => ({
      ...prev,
      custom_perks: prev.custom_perks.map((p, i) => i === idx ? { ...p, ...patch } : p),
    }));
  }, []);

  // Stage 73 — one-click starter for gross-based groups.
  // Fills in the 5 standard splits the user described:
  //   Basic = 50% of Gross
  //   HRA   = 50% of Basic
  //   Conveyance = ₹19,200/yr (₹1,600/mo — IT exempt cap)
  //   Medical    = ₹15,000/yr (₹1,250/mo — IT exempt cap)
  //   Special Allowance = balancing
  // Plus statutory employer-side rows for PF/ESI so CTC L1 derivation is
  // visible immediately. Admin can edit %s / values after applying.
  const applyGrossBasedTemplate = useCallback(() => {
    if (draft.salary_heads.length > 0) {
      if (!window.confirm('This will REPLACE the current salary heads with the standard template. Continue?')) return;
    }
    setDraft(prev => ({
      ...prev,
      salary_heads: [
        { code: 'BASIC',  name: 'Basic Salary',       type: 'earning',                 calc: 'percentage', pct_of: 'GROSS', pct_val: 50, value: 0,     ctc_component: true  },
        { code: 'HRA',    name: 'House Rent Allow.',  type: 'earning',                 calc: 'percentage', pct_of: 'BASIC', pct_val: 50, value: 0,     ctc_component: true  },
        { code: 'CONV',   name: 'Conveyance',         type: 'earning',                 calc: 'fixed',      pct_of: 'GROSS', pct_val: 0,  value: 19200, ctc_component: true  },
        { code: 'MEDA',   name: 'Medical Allowance',  type: 'earning',                 calc: 'fixed',      pct_of: 'GROSS', pct_val: 0,  value: 15000, ctc_component: true  },
        { code: 'SPA',    name: 'Special Allowance',  type: 'earning',                 calc: 'balancing',  pct_of: 'GROSS', pct_val: 0,  value: 0,     ctc_component: true  },
        { code: 'PF_ER',  name: 'Employer PF',        type: 'employer_contribution',   calc: 'percentage', pct_of: 'BASIC', pct_val: 12, value: 0,     ctc_component: false },
        { code: 'ESI_ER', name: 'Employer ESI',       type: 'employer_contribution',   calc: 'percentage', pct_of: 'GROSS', pct_val: 3.25, value: 0,   ctc_component: false },
      ],
    }));
  }, [draft.salary_heads.length]);

  // ---- Live calculation ----
  // Statutory caps (defaults match Indian payroll: PF ₹15K / ESI ₹21K monthly).
  // Annualised here once so the multi-pass loop below stays cheap.
  const pfCfg  = statutoryConfig?.pf  || { wage_ceiling: 15000, is_active: true };
  const esiCfg = statutoryConfig?.esi || { wage_ceiling: 21000, is_active: true };
  const pfCeilingAnnual  = (Number(pfCfg.wage_ceiling)  || 15000) * 12;
  const esiCeilingMonthly = Number(esiCfg.wage_ceiling) || 21000;

  // Lookup of head_type by code from the company's salary-heads master — used
  // when computing the GROSS reference so we sum only EARNING heads.
  const typeByCode = useMemo(() => {
    const m = {};
    for (const opt of salaryHeadMaster || []) {
      const code = (opt.code || '').toUpperCase();
      const t = ((opt.head_type || opt.type || 'earning') + '').toLowerCase();
      if (code) m[code] = t;
    }
    return m;
  }, [salaryHeadMaster]);

  const preview = useMemo(() => {
    // ----- Anchor selection by calc method (Stage 73) -----
    // gross_based: user enters annual gross. CTC L1 = gross + ER contribs.
    //              Final CTC = CTC L1 + perks.
    // ctc_based : user enters annual CTC. % heads reference CTC; gross is derived.
    const method = draft.calc_method || 'gross_based';
    const isGrossBased = method === 'gross_based';
    const gross_input = Number(draft.gross_amount) || 0;
    const ctc_input   = Number(draft.ctc_amount)   || 0;

    // Heads we'll mutate during the multi-pass resolver.
    const heads = draft.salary_heads.map(h => ({ ...h, _annual: 0, _error: null, _cap_note: null }));

    // Master-aware head_type resolver — falls back to row's local type.
    const headTypeOf = (h) => {
      const c = (h.code || '').toUpperCase();
      return typeByCode[c] || (h.type || 'earning').toLowerCase();
    };

    // True annual gross (sum of EARNING heads currently resolved, excluding self).
    // For gross_based mode this should converge to the user-entered gross_input
    // after the balancing row absorbs the remainder.
    const grossAnnualExcluding = (selfHead) => {
      let s = 0;
      for (const oh of heads) {
        if (oh === selfHead) continue;
        if (!oh.code) continue;
        if (headTypeOf(oh) !== 'earning') continue;
        s += oh._annual || 0;
      }
      return s;
    };

    // ref dict — what `pct_of` references resolve to.
    const ref = { CTC: ctc_input, GROSS_INPUT: gross_input };

    // The "GROSS" base used by % heads. In gross-based mode this is the
    // entered annual gross (so PF=12% of GROSS works deterministically before
    // the balancing pass converges). In ctc-based mode, fall back to the
    // self-excluding sum so legacy groups keep working.
    const grossBase = (selfHead) => isGrossBased ? gross_input : grossAnnualExcluding(selfHead);

    // Multi-pass resolver — 5 passes catches Basic→HRA→PF→ESI cascades.
    for (let pass = 0; pass < 5; pass++) {
      let changed = false;
      for (const h of heads) {
        if (!h.code && h.calc !== 'balancing') continue;
        if (h.calc === 'fixed') {
          const v = Number(h.value) || 0;
          if (h._annual !== v) { h._annual = v; changed = true; }
          if (h.code) ref[h.code.toUpperCase()] = v;
        } else if (h.calc === 'percentage') {
          const pctOf = (h.pct_of || '').toUpperCase().trim();
          let base = 0;
          if      (pctOf === 'CTC')      base = ctc_input;
          else if (pctOf === 'BASIC_DA') base = (ref.BASIC || 0) + (ref.DA || 0);
          else if (pctOf === 'GROSS')    base = grossBase(h);
          else if (ref[pctOf] != null)   base = ref[pctOf];
          else { h._error = `Unknown reference: ${pctOf}`; continue; }

          // ----- Statutory cap & eligibility checks (Stage 68) -----
          let capNote = null;
          let skip = false;
          if (isPfHeadCode(h.code) && pfCfg.is_active !== false) {
            if (base > pfCeilingAnnual) {
              capNote = `PF wage capped at ₹${(pfCfg.wage_ceiling || 15000).toLocaleString('en-IN')}/mo`;
              base = pfCeilingAnnual;
            }
          }
          if (isEsiHeadCode(h.code) && esiCfg.is_active !== false) {
            const monthlyBase = base / 12;
            if (monthlyBase > esiCeilingMonthly) {
              capNote = `ESI not applicable (gross > ₹${esiCeilingMonthly.toLocaleString('en-IN')}/mo)`;
              h._cap_note = capNote;
              if (h._annual !== 0) { h._annual = 0; changed = true; }
              if (h.code) ref[h.code.toUpperCase()] = 0;
              skip = true;
            }
          }
          if (skip) continue;

          const pct = Number(h.pct_val) || 0;
          const v = base * pct / 100;
          if (Math.abs(h._annual - v) > 0.01) { h._annual = v; changed = true; }
          if (h.code) ref[h.code.toUpperCase()] = v;
          if (capNote) h._cap_note = capNote;
        }
      }
      if (!changed) break;
    }

    // ----- Balancing row -----
    // gross_based : balancing absorbs (gross_input − sum of other EARNING heads)
    //               so gross = exact user-entered value.
    // ctc_based   : balancing absorbs (ctc − sum of other CTC component heads)
    //               so CTC = exact user-entered value (legacy behaviour).
    const ctcHeads = heads.filter(h => h.ctc_component);
    const balancingRow = heads.find(h => h.calc === 'balancing'); // search ALL heads, not just ctcHeads
    if (balancingRow) {
      if (isGrossBased) {
        // Gross-fit: sum earning heads excluding the balancing row, fill remainder.
        const otherEarn = heads
          .filter(h => h !== balancingRow && h.code && headTypeOf(h) === 'earning')
          .reduce((s, h) => s + (h._annual || 0), 0);
        balancingRow._annual = Math.max(0, gross_input - otherEarn);
      } else {
        const otherCtc = ctcHeads.filter(h => h !== balancingRow).reduce((s, h) => s + (h._annual || 0), 0);
        balancingRow._annual = Math.max(0, ctc_input - otherCtc);
      }
      if (balancingRow.code) ref[balancingRow.code.toUpperCase()] = balancingRow._annual;
    }

    // ----- Aggregates -----
    // Gross (annual) — sum of earning heads only. Drives ESI eligibility, OT, payslip.
    const gross_annual = heads
      .filter(h => h.code && headTypeOf(h) === 'earning')
      .reduce((s, h) => s + (h._annual || 0), 0);

    // Employer contributions — auto-detected. Either via master's head_type
    // ('employer_contribution') or via code suffix heuristic (`_ER` / `_EMPLOYER`).
    // These add to CTC but NOT to gross.
    const employer_contribs_annual = heads
      .filter(h => h.code && (headTypeOf(h) === 'employer_contribution' || isEmployerSideHead(h.code)))
      .reduce((s, h) => s + (h._annual || 0), 0);

    // Custom perks — entered as monthly_amount; sum annualised.
    // Standard `include_*` booleans are not yet rate-driven so they don't
    // contribute a number — the user can replace them with custom_perks rows
    // for explicit values.
    const perks_annual = (draft.custom_perks || []).reduce((s, p) => {
      const monthly = Number(p?.monthly_amount) || 0;
      return s + (monthly * 12);
    }, 0);

    // ----- 3-tier KPI -----
    // For gross_based: Gross is the anchor, CTC L1 = Gross + ER, Final = CTC L1 + Perks.
    // For ctc_based:   CTC is the anchor (sum of ctc_component heads), Gross is derived,
    //                  CTC L1 and Final shown for parity.
    const gross_display = isGrossBased ? gross_input : gross_annual;
    const ctc_l1   = gross_display + employer_contribs_annual;
    const ctc_final = ctc_l1 + perks_annual;

    // For ctc-based legacy mode we still expose the In-CTC / delta numbers.
    const ctc_in = ctcHeads.reduce((s, h) => s + (h._annual || 0), 0);
    const above_ctc = heads.filter(h => !h.ctc_component).reduce((s, h) => s + (h._annual || 0), 0);
    const target_anchor = isGrossBased ? gross_input : ctc_input;
    const delta = isGrossBased ? (gross_annual - gross_input) : (ctc_in - ctc_input);

    // ---- OT preview ----
    let ot_per_day = 0;
    let ot_total = 0;
    let ot_formula_text = '';
    if (draft.ot_enabled) {
      const monthly_basic = (ref.BASIC || 0) / 12;
      const monthly_hra   = (ref.HRA   || 0) / 12;
      const monthly_gross = gross_display / 12;
      let num_value = 0;
      let num_label = '';
      switch (draft.ot_numerator) {
        case 'BASIC':
          num_value = monthly_basic; num_label = `Basic (₹${num_value.toFixed(0)}/mo)`; break;
        case 'CTC_MINUS_HRA':
          num_value = monthly_gross - monthly_hra;
          num_label = `(Gross−HRA) (₹${num_value.toFixed(0)}/mo)`; break;
        case 'GROSS':
          num_value = monthly_gross; num_label = `Gross (₹${num_value.toFixed(0)}/mo)`; break;
        case 'CUSTOM':
          if (/^\d+(\.\d+)?$/.test(draft.ot_numerator_custom || '')) {
            num_value = Number(draft.ot_numerator_custom);
            num_label = `₹${num_value}`;
          } else {
            const code = (draft.ot_numerator_custom || '').toUpperCase();
            num_value = (ref[code] || 0) / 12;
            num_label = `${code} (₹${num_value.toFixed(0)}/mo)`;
          }
          break;
        default: num_value = 0;
      }
      const factor = Number(draft.ot_numerator_factor) || 1;
      const num_after_factor = num_value * factor;
      let div_value = 1;
      let div_label = '';
      switch (draft.ot_divisor) {
        case 'BASE_DAYS':
          div_value = Number(draft.base_days_fixed) || 30;
          div_label = `Base Days (${div_value})`; break;
        case '240':
          div_value = 240; div_label = '240 (hours)'; break;
        case 'CALENDAR_DAYS':
          div_value = 30; div_label = 'Calendar Days (30 avg)'; break;
        case 'CUSTOM':
          div_value = Number(draft.ot_divisor_custom) || 1;
          div_label = `Custom (${div_value})`; break;
        default: div_value = 1;
      }
      const mult = Number(draft.ot_multiplier) || 1;
      ot_per_day = (num_after_factor / div_value) * mult;
      ot_total = ot_per_day * (Number(draft.ot_demo_days) || 0);
      const factor_part = factor !== 1 ? ` × ${factor}` : '';
      ot_formula_text = `(${num_label}${factor_part}) ÷ ${div_label} × ${mult}× × OT_Days`;
    }

    return {
      heads, method, isGrossBased,
      // Legacy fields (kept for ctc_based mode and existing UI)
      ctc_in, above_ctc,
      ctc: ctc_input,
      delta,
      // Stage 73 — new 3-tier breakdown
      gross_input,
      gross_annual,                // sum of earning heads (for ctc_based, used for ESI eligibility)
      gross_display,               // what to show as "Gross" in the KPI
      gross_monthly: gross_display / 12,
      employer_contribs_annual,
      perks_annual,
      ctc_l1, ctc_final,
      target_anchor,
      // OT
      ot_per_day, ot_total, ot_formula_text,
    };
  }, [draft.salary_heads, draft.ctc_amount, draft.gross_amount, draft.calc_method,
      draft.custom_perks, draft.ot_enabled,
      draft.ot_numerator, draft.ot_numerator_custom, draft.ot_numerator_factor,
      draft.ot_divisor, draft.ot_divisor_custom, draft.ot_multiplier,
      draft.ot_demo_days, draft.base_days_fixed,
      typeByCode, pfCeilingAnnual, esiCeilingMonthly,
      pfCfg.wage_ceiling, pfCfg.is_active, esiCfg.is_active]);

  const balancingCount = draft.salary_heads.filter(h => h.calc === 'balancing' && h.ctc_component).length;

  const submit = () => {
    if (!draft.name || !draft.code || !draft.effective_from) {
      return alert('Name, Code and Effective Date are required');
    }
    if (balancingCount > 1) {
      return alert('Only ONE salary head can be "Balancing" per group');
    }
    // Normalise numbers before sending to backend
    const heads = draft.salary_heads.map(h => ({
      code: h.code, name: h.name, type: h.type,
      calc: h.calc, pct_of: h.pct_of, pct_val: Number(h.pct_val) || 0,
      value: Number(h.value) || 0,
      ctc_component: h.ctc_component !== false,
    }));
    // Strip only the truly UI-only preview field — `ot_demo_days` is a
    // "what-if N OT days" slider for live calc. `ctc_amount` IS persisted
    // now (Stage 72) so it round-trips correctly on Edit and anchors
    // percentage-based head calculations.
    const {
      ot_demo_days: _uiOtDemo,
      ...rest
    } = draft;
    const toIntOrNull = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      ...rest,
      salary_heads: heads,
      // Coerce CTC to a number — the input may surface as a string from the
      // <input type="number"> binding. Empty/invalid → 0 so backend Pydantic
      // (float) accepts cleanly. The live preview also reads draft.ctc_amount
      // and survives because Number(draft.ctc_amount) || 0 is used there.
      ctc_amount: Number(draft.ctc_amount) || 0,
      // Stage 73 — gross-based anchor. Coerced same way; defaults to 0 when
      // the group is ctc_based / per_day_wage and gross input is unused.
      gross_amount: Number(draft.gross_amount) || 0,
      base_days_fixed: Number(draft.base_days_fixed) || 30,
      ot_numerator_factor: Number(draft.ot_numerator_factor) || 1,
      ot_divisor_custom: Number(draft.ot_divisor_custom) || 0,
      ot_multiplier: Number(draft.ot_multiplier) || 1,
      pay_cycle_start_day: toIntOrNull(draft.pay_cycle_start_day),
      pay_cycle_end_day: toIntOrNull(draft.pay_cycle_end_day),
      pay_cycle_label: (draft.pay_cycle_label || '').trim() || null,
      parent_group: (draft.parent_group || '').trim() || null,
      sub_group: (draft.sub_group || '').trim() || null,
      calc_method: draft.calc_method || 'ctc_based',
      wage_basis_act: (draft.wage_basis_act || '').trim() || null,
      wage_basis_location: (draft.wage_basis_location || '').trim() || null,
    };
    onCreate(payload);
  };

  const headOptions = useMemo(() => salaryHeadMaster.map(m => ({
    code: (m.code || '').toUpperCase(),
    name: m.name || m.code,
    type: m.type || m.head_type || 'earning',
  })), [salaryHeadMaster]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl border-2 border-blue-200 w-full max-w-6xl max-h-[92vh] overflow-y-auto p-5 space-y-4 shadow-2xl"
        data-testid="ctc-new-group-form" onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-blue-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> {initial ? `Edit CTC Group — ${initial.name}` : 'Add New CTC Group'}
          </h4>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <LabelledInput label="Group Name *" value={draft.name}
            onChange={v => set({ name: v })} placeholder="e.g. O&M – Mech Tier 1"
            testid="ctc-new-name" />
          <LabelledInput label={initial ? 'Code (locked)' : 'Code *'} value={draft.code} mono
            onChange={v => set({ code: v.toUpperCase() })} placeholder="e.g. OM_MECH_T1"
            disabled={!!initial}
            testid="ctc-new-code" />
          <LabelledInput label="Effective From *" type="date" value={draft.effective_from}
            onChange={v => set({ effective_from: v })} testid="ctc-new-effective" />
          <LabelledInput label="Description" value={draft.description}
            onChange={v => set({ description: v })} placeholder="Optional"
            testid="ctc-new-desc" />
          {/* Anchor input — method-aware. gross_based: edit annual gross.
              ctc_based / legacy: edit annual CTC. Stage 73. */}
          {(draft.calc_method || 'gross_based') === 'gross_based' ? (
            <LabelledInput label="Annual Gross (₹/yr) *" type="number" mono value={draft.gross_amount}
              onChange={v => set({ gross_amount: v })} placeholder="300000"
              testid="ctc-gross-amount" />
          ) : (
            <LabelledInput label="Annual CTC (₹/yr)" type="number" mono value={draft.ctc_amount}
              onChange={v => set({ ctc_amount: v })} placeholder="600000"
              testid="ctc-preview-amount" />
          )}
        </div>

        {/* Group categorisation + pay cycle — used for bulk payroll selection */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-medium text-indigo-700">Parent Group</label>
              <input value={draft.parent_group || ''}
                onChange={e => set({ parent_group: e.target.value })}
                placeholder="e.g. Staff / O&M / Contract"
                data-testid="ctc-parent-group"
                className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-indigo-700">Sub-Group (optional)</label>
              <input value={draft.sub_group || ''}
                onChange={e => set({ sub_group: e.target.value })}
                placeholder="e.g. Plumbing / Electrical / Tier 1"
                data-testid="ctc-sub-group"
                className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div className="text-[10px] text-indigo-700 self-end pb-1">
              💡 Use Parent / Sub-Group to <b>bulk-select</b> CTC structures during payroll run.
            </div>
          </div>

          {/* Salary Calculation Method — Phase 1 of two-method engine */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-indigo-200">
            <div className="md:col-span-3 text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">⚙️ Salary Calculation Method</div>
            <div>
              <label className="text-[10px] font-medium text-indigo-700">Method</label>
              <select value={draft.calc_method || 'gross_based'}
                onChange={e => set({ calc_method: e.target.value })}
                data-testid="ctc-calc-method"
                className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm bg-white">
                <option value="gross_based">Gross-Based (Staff – recommended)</option>
                <option value="ctc_based">CTC-Based (Legacy)</option>
                <option value="per_day_wage">Per-Day Wage (O&M / Labour)</option>
                <option value="fixed_gross">Fixed Gross</option>
                <option value="custom">Custom (formula-only)</option>
              </select>
            </div>
            {(draft.calc_method === 'per_day_wage') && (
              <>
                <div>
                  <label className="text-[10px] font-medium text-indigo-700">Wage Basis Act</label>
                  <input value={draft.wage_basis_act || ''}
                    onChange={e => set({ wage_basis_act: e.target.value })}
                    placeholder="e.g. TN Industrial Establishment Act"
                    list="wage-acts-list"
                    data-testid="ctc-wage-act"
                    className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm" />
                  <datalist id="wage-acts-list">
                    <option value="Central Minimum Wages" />
                    <option value="TN Industrial Establishment Act" />
                    <option value="TN Shops and Establishment Act" />
                    <option value="Karnataka Minimum Wages Act" />
                    <option value="Kerala Minimum Wages Act" />
                    <option value="Delhi Minimum Wages Act" />
                  </datalist>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-indigo-700">Default Location</label>
                  <input value={draft.wage_basis_location || ''}
                    onChange={e => set({ wage_basis_location: e.target.value })}
                    placeholder="e.g. Chennai / PAN-India"
                    data-testid="ctc-wage-location"
                    className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm" />
                </div>
                <div className="md:col-span-3 text-[11px] bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                  🪙 Per-Day Wage: <b>Basic = ₹/day × payable_days</b>, then HRA / Bonus / Exgratia / EL Encashment compute as % of Basic per the salary heads below.
                  Daily rate is resolved from <b>Wage Master → Wage Rates</b> using the employee's <b>skill_category</b> + this group's act/location.
                  Each employee can override via <b>Employee Master → Daily Rate Override</b>.
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-3 border-t border-indigo-200">
            <div className="md:col-span-4 text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">📅 Pay Cycle (cutoff dates) — leave blank to use company default</div>
            <div>
              <label className="text-[10px] font-medium text-indigo-700">Cycle Start Day</label>
              <input type="number" min="1" max="31" value={draft.pay_cycle_start_day || ''}
                onChange={e => set({ pay_cycle_start_day: e.target.value })}
                placeholder="1"
                data-testid="ctc-cycle-start"
                className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-indigo-700">Cycle End Day</label>
              <input type="number" min="1" max="31" value={draft.pay_cycle_end_day || ''}
                onChange={e => set({ pay_cycle_end_day: e.target.value })}
                placeholder="31"
                data-testid="ctc-cycle-end"
                className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-indigo-700">Display Label</label>
              <input value={draft.pay_cycle_label || ''}
                onChange={e => set({ pay_cycle_label: e.target.value })}
                placeholder="1st-31st"
                data-testid="ctc-cycle-label"
                className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div className="text-[10px] text-indigo-700 self-end pb-1">
              💡 Quick presets:{' '}
              <button type="button" onClick={() => set({ pay_cycle_start_day: 1, pay_cycle_end_day: 31, pay_cycle_label: '1st-31st' })} className="underline mr-2">1-31</button>
              <button type="button" onClick={() => set({ pay_cycle_start_day: 16, pay_cycle_end_day: 15, pay_cycle_label: '16th-15th' })} className="underline mr-2">16-15</button>
              <button type="button" onClick={() => set({ pay_cycle_start_day: 21, pay_cycle_end_day: 20, pay_cycle_label: '21st-20th' })} className="underline">21-20</button>
            </div>
          </div>
        </div>

        {/* Salary Heads */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-xs font-semibold text-slate-700">Salary Heads</label>
              <span className="ml-2 text-[10px] text-slate-500">tick <b>In CTC</b> for heads that form the CTC package; untick for Above-CTC items (bonus / reimb / insurance)</span>
            </div>
            <div className="flex gap-1">
              {/* Stage 73 — one-click standard template for gross-based groups */}
              {(draft.calc_method || 'gross_based') === 'gross_based' && (
                <button data-testid="ctc-apply-template-btn"
                  onClick={applyGrossBasedTemplate}
                  className="flex items-center gap-1 px-2 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded text-[10px] font-medium"
                  title="Pre-fill Basic / HRA / Conveyance / Medical / Special-Allowance with sensible defaults">
                  ✨ Apply Standard Template
                </button>
              )}
              <button data-testid="ctc-add-head-btn" onClick={addHead}
                className="flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-medium">
                <Plus className="w-3 h-3" /> Add Head
              </button>
            </div>
          </div>

          {/* Gross-based onboarding banner — visible whenever the structure
              is empty so the admin knows where to start. */}
          {(draft.calc_method || 'gross_based') === 'gross_based' && draft.salary_heads.length === 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-[11px] text-emerald-900 mb-2" data-testid="ctc-gross-onboarding">
              <b>Gross-based mode:</b> enter <b>Annual Gross</b> above, then click <b>✨ Apply Standard Template</b> to pre-fill
              Basic (50% of Gross), HRA (50% of Basic), Conveyance (₹19,200/yr), Medical (₹15,000/yr),
              and Special Allowance (balancing). Add Employer PF / ESI rows below to derive <b>CTC L1 = Gross + ER PF + ER ESI</b>,
              then perks for <b>Final CTC</b>.
            </div>
          )}

          {draft.salary_heads.length === 0 ? (
            <p className="text-xs text-slate-400">No salary heads added. Click <strong>+ Add Head</strong> above.</p>
          ) : (
            <div className="space-y-1.5">
              <div className="grid grid-cols-12 gap-1 px-2 text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                <div className="col-span-3">Salary Head</div>
                <div className="col-span-1 text-center">In CTC</div>
                <div className="col-span-2">Calc Type</div>
                <div className="col-span-2">% Of</div>
                <div className="col-span-1">% / Amt</div>
                <div className="col-span-2 text-right">Computed (₹/yr)</div>
                <div className="col-span-1"></div>
              </div>
              {draft.salary_heads.map((h, i) => (
                <HeadRow key={i} h={h} idx={i}
                  computed={preview.heads[i]}
                  headOptions={headOptions}
                  onChange={(patch) => setHead(i, patch)}
                  onRemove={() => removeHead(i)} />
              ))}
            </div>
          )}
        </div>

        {/* Live preview — Stage 73 3-tier breakdown for gross-based mode */}
        <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-800 mb-2">
            <Calculator className="w-3.5 h-3.5" />
            {preview.isGrossBased
              ? <>Live Preview · <span className="text-emerald-700">Gross-based mode</span> @ Annual Gross = ₹ {Number(draft.gross_amount || 0).toLocaleString('en-IN')}</>
              : <>Live Preview · CTC-based @ CTC = ₹ {Number(draft.ctc_amount || 0).toLocaleString('en-IN')}</>
            }
          </div>

          {/* 3-tier KPI: Gross → CTC L1 → Final CTC */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs" data-testid="ctc-kpi-row">
            <PreviewStat label="Gross (annual)"            value={preview.gross_display}              color="emerald" testid="kpi-gross" />
            <PreviewStat label="+ Employer PF / ESI"        value={preview.employer_contribs_annual}   color="blue"    testid="kpi-er" />
            <PreviewStat label="= CTC Level 1"              value={preview.ctc_l1}                     color="indigo"  testid="kpi-ctc-l1" />
            <PreviewStat label="+ Perks (CTC-only)"         value={preview.perks_annual}               color="amber"   testid="kpi-perks" />
            <PreviewStat label="= Final CTC"                value={preview.ctc_final}                  color="rose"    testid="kpi-final" />
          </div>

          {/* Monthly Gross helper line — payslip-style. */}
          <div className="mt-2 text-[10px] text-slate-600" data-testid="ctc-monthly-gross">
            ≡ <b>Monthly Gross</b>: ₹{(preview.gross_monthly).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            {preview.isGrossBased && balancingCount === 0 && Math.abs(preview.delta) > 1 && (
              <span className="ml-2 text-amber-700">
                · Earnings sum to ₹{preview.gross_annual.toLocaleString('en-IN')} but target is ₹{preview.gross_input.toLocaleString('en-IN')} —
                add a head with <b>Calc = Balancing</b> to auto-fill the remainder.
              </span>
            )}
          </div>

          {/* For ctc-based legacy mode, also show the In-CTC / Above-CTC stats. */}
          {!preview.isGrossBased && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-3 pt-3 border-t border-blue-200">
              <PreviewStat label="In-CTC total" value={preview.ctc_in} color="blue" />
              <PreviewStat label="Above-CTC"    value={preview.above_ctc} color="amber" />
              <PreviewStat label="Target CTC"   value={preview.ctc} color="slate" />
              <PreviewStat label={preview.delta === 0 ? 'Balance' : (preview.delta > 0 ? 'OVER by' : 'UNDER by')}
                value={Math.abs(preview.delta)}
                color={Math.abs(preview.delta) < 1 ? 'emerald' : 'rose'} />
            </div>
          )}

          {/* Statutory cap legend — visible whenever PF/ESI heads appear in the structure. */}
          <div className="mt-2 flex flex-wrap gap-2 text-[10px]" data-testid="statutory-cap-legend">
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded">
              💼 PF cap: ₹{Number(pfCfg.wage_ceiling || 15000).toLocaleString('en-IN')}/mo
            </span>
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded">
              🏥 ESI cap: ₹{Number(esiCfg.wage_ceiling || 21000).toLocaleString('en-IN')}/mo (eligibility cut-off)
            </span>
            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
              📐 Gross = sum of earning heads only (deductions excluded)
            </span>
          </div>
          {!preview.isGrossBased && Math.abs(preview.delta) >= 1 && !balancingCount && (
            <div className="mt-2 text-[11px] text-amber-700 flex items-center gap-1">
              <Info className="w-3 h-3" /> In-CTC total doesn't match CTC. Add a head with <b>Calc = Balancing</b> to auto-fill the remainder.
            </div>
          )}
          {balancingCount > 1 && (
            <div className="mt-2 text-[11px] text-rose-700 flex items-center gap-1">
              <Info className="w-3 h-3" /> Only one head can be <b>Balancing</b>.
            </div>
          )}
        </div>

        {/* F&F Settlement Salary Proration Method */}
        <div className="bg-indigo-50/90 rounded-lg p-3 border border-indigo-200 space-y-2">
          <div className="text-xs font-semibold text-indigo-900 flex items-center gap-2">
            🏷️ F&F Settlement Salary Proration Method
            <span className="text-[10px] font-normal text-indigo-700">
              — Configures how partial-month salary is calculated when employees in this group resign.
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-indigo-800">Proration Method</label>
              <select
                value={draft.salary_proration_method || 'WAGE_DAYS'}
                onChange={e => set({ salary_proration_method: e.target.value })}
                data-testid="ctc-salary-proration-method"
                className="w-full mt-1 border border-indigo-300 rounded px-2 py-1.5 text-xs bg-white font-medium text-slate-800"
              >
                <option value="WAGE_DAYS">Wage Days (Prorate by Wage Period Cycle Days, e.g. 31 days)</option>
                <option value="BASE_DAYS">Base Days (Prorate by Settlement Month Calendar Days, e.g. 30 for April, 31 for May)</option>
              </select>
            </div>
            <div className="bg-white rounded border border-indigo-200 p-2 text-[11px] text-slate-600 flex items-center">
              {draft.salary_proration_method === 'BASE_DAYS' ? (
                <span>
                  <strong>Base Days Mode:</strong> Salary = <code>Gross × Worked Days ÷ Calendar Days of Exit Month</code> (e.g., April = 30 days).
                </span>
              ) : (
                <span>
                  <strong>Wage Days Mode:</strong> Salary = <code>Gross × Worked Days ÷ Wage Period Days</code> (e.g., 21-Mar to 20-Apr = 31 days).
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Base Days */}
        <details className="bg-emerald-50 rounded-lg p-3 border border-emerald-200" open>
          <summary className="text-xs font-semibold text-emerald-800 cursor-pointer flex items-center gap-2">
            📅 Base Days (per pay group)
            📅 Base Days (per pay group)
            <span className="text-[10px] font-normal text-emerald-700">— admin-fixed Base days. For monthly-CTC staff: Pay Days = Base − LOP + Additional; earnings prorate against Base. (O&M / per-day-wage workers have their own resolution.) Leave 30 for the standard Indian payroll convention.</span>
          </summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-medium text-slate-600">Method</label>
              <select value={draft.base_days_method}
                onChange={e => set({ base_days_method: e.target.value })}
                data-testid="ctc-base-days-method"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs bg-white">
                <option value="fixed">Fixed (e.g. 26 / 30)</option>
                <option value="calendar">Calendar Days (28 / 30 / 31)</option>
                <option value="working_days">Working Days (calendar − weekly off − holidays)</option>
              </select>
            </div>
            {draft.base_days_method === 'fixed' && (
              <div>
                <label className="text-[10px] font-medium text-slate-600">Fixed Base Days</label>
                <input type="text" inputMode="decimal" value={draft.base_days_fixed}
                  onChange={e => set({ base_days_fixed: e.target.value.replace(/[^0-9.]/g, '') })}
                  placeholder="30 or 26"
                  data-testid="ctc-base-days-fixed"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs font-mono" />
              </div>
            )}
            <div className="bg-white rounded border border-emerald-200 p-2">
              <div className="text-[10px] text-slate-500">Per-day Basic ≈</div>
              <div className="text-sm font-mono font-bold text-emerald-700">
                ₹ {(((preview.heads.find(h => (h.code || '').toUpperCase() === 'BASIC')?._annual || 0) / 12) /
                    (Number(draft.base_days_fixed) || 30)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-emerald-700">
            💡 Bulk upload? Go to <a href="/business-controls?tab=ctc" className="underline">Salary Structures (CTC Groups)</a> → click <b>📥 Bulk Update Base Days</b> at the top of the list.
          </div>
        </details>

        {/* OT Calculation */}
        <details className="bg-amber-50 rounded-lg p-3 border border-amber-200" open={!!draft.ot_enabled}>
          <summary className="text-xs font-semibold text-amber-800 cursor-pointer flex items-center gap-2">
            ⏱️ OT Calculation
            <span className="text-[10px] font-normal text-amber-700">— flexible per-group formula via dropdowns (no code required)</span>
          </summary>
          <div className="mt-3">
            <label className="flex items-center gap-2 text-xs text-slate-700 mb-3">
              <input type="checkbox" checked={!!draft.ot_enabled}
                onChange={e => set({ ot_enabled: e.target.checked })}
                data-testid="ctc-ot-enabled"
                className="rounded border-slate-300" />
              <b>Enable OT for this CTC group</b>
            </label>

            {draft.ot_enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end mb-3">
                  <div>
                    <label className="text-[10px] font-medium text-slate-600">Numerator</label>
                    <select value={draft.ot_numerator}
                      onChange={e => set({ ot_numerator: e.target.value })}
                      data-testid="ctc-ot-numerator"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs bg-white">
                      <option value="BASIC">Basic (monthly)</option>
                      <option value="CTC_MINUS_HRA">CTC − HRA (monthly)</option>
                      <option value="GROSS">Gross (monthly)</option>
                      <option value="CUSTOM">Custom (head code or ₹)</option>
                    </select>
                  </div>

                  {draft.ot_numerator === 'CUSTOM' && (
                    <div>
                      <label className="text-[10px] font-medium text-slate-600">Custom value</label>
                      <input value={draft.ot_numerator_custom || ''}
                        onChange={e => set({ ot_numerator_custom: e.target.value.toUpperCase() })}
                        placeholder="BASIC or 25000"
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs font-mono" />
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] font-medium text-slate-600">× factor</label>
                    <input type="text" inputMode="decimal" value={draft.ot_numerator_factor}
                      onChange={e => set({ ot_numerator_factor: e.target.value.replace(/[^0-9.]/g, '') })}
                      placeholder="1 or 0.88"
                      data-testid="ctc-ot-num-factor"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs font-mono" />
                  </div>

                  <div>
                    <label className="text-[10px] font-medium text-slate-600">Divisor</label>
                    <select value={draft.ot_divisor}
                      onChange={e => set({ ot_divisor: e.target.value })}
                      data-testid="ctc-ot-divisor"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs bg-white">
                      <option value="BASE_DAYS">Base Days (per group)</option>
                      <option value="240">240 (hours)</option>
                      <option value="CALENDAR_DAYS">Calendar Days</option>
                      <option value="CUSTOM">Custom number</option>
                    </select>
                  </div>

                  {draft.ot_divisor === 'CUSTOM' && (
                    <div>
                      <label className="text-[10px] font-medium text-slate-600">Custom divisor</label>
                      <input type="text" inputMode="decimal" value={draft.ot_divisor_custom}
                        onChange={e => set({ ot_divisor_custom: e.target.value.replace(/[^0-9.]/g, '') })}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs font-mono" />
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] font-medium text-slate-600">Multiplier</label>
                    <select value={draft.ot_multiplier}
                      onChange={e => set({ ot_multiplier: e.target.value })}
                      data-testid="ctc-ot-multiplier"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs bg-white">
                      <option value="1">1× (regular)</option>
                      <option value="1.5">1.5×</option>
                      <option value="2">2× (statutory)</option>
                    </select>
                  </div>
                </div>

                {/* Formula text + live preview */}
                <div className="bg-white border border-amber-300 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1">Resolved Formula</div>
                  <code className="text-xs text-slate-800 break-words">{preview.ot_formula_text || '—'}</code>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <div>
                      <label className="text-[10px] font-medium text-slate-600">Demo OT Days</label>
                      <input type="text" inputMode="decimal" value={draft.ot_demo_days}
                        onChange={e => set({ ot_demo_days: e.target.value.replace(/[^0-9.]/g, '') })}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs font-mono" />
                    </div>
                    <div className="bg-amber-50 rounded p-2">
                      <div className="text-[10px] text-amber-700">OT per day</div>
                      <div className="text-sm font-mono font-bold text-amber-800">
                        ₹ {Number(preview.ot_per_day || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="bg-amber-100 rounded p-2">
                      <div className="text-[10px] text-amber-700">OT for {draft.ot_demo_days || 0} day(s)</div>
                      <div className="text-sm font-mono font-bold text-amber-900">
                        ₹ {Number(preview.ot_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="text-[10px] text-slate-500">Quick presets:</span>
                    <button type="button" data-testid="ctc-ot-preset-basic"
                      onClick={() => set({ ot_numerator: 'BASIC', ot_numerator_factor: 1, ot_divisor: 'BASE_DAYS', ot_multiplier: 2 })}
                      className="text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-0.5 rounded">
                      (Basic ÷ Base Days) × OT × 2
                    </button>
                    <button type="button" data-testid="ctc-ot-preset-ctchra"
                      onClick={() => set({ ot_numerator: 'CTC_MINUS_HRA', ot_numerator_factor: 0.88, ot_divisor: 'BASE_DAYS', ot_multiplier: 2 })}
                      className="text-[10px] bg-purple-100 hover:bg-purple-200 text-purple-700 px-2 py-0.5 rounded">
                      ((CTC−HRA)×0.88) × 2 ÷ Base Days × OT
                    </button>
                    <button type="button" data-testid="ctc-ot-preset-gross"
                      onClick={() => set({ ot_numerator: 'GROSS', ot_numerator_factor: 1, ot_divisor: '240', ot_multiplier: 1.5 })}
                      className="text-[10px] bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-2 py-0.5 rounded">
                      (Gross ÷ 240) × 1.5×
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </details>

        {/* Stage 73.2 — Perks section redesigned
            Old design had 11 preset-perk CHECKBOXES (no rupee value, did nothing)
            + a separate Custom Perks list. Replaced with a single unified list
            where every row carries an actual ₹/month amount, plus quick-add
            chips for the 11 common perks. A bright amber banner above the list
            makes the CTC-only nature crystal clear so admins don't expect
            these to show on the payslip automatically. */}
        <details className="bg-amber-50 rounded-lg p-3 border border-amber-200" open>
          <summary className="text-xs font-semibold text-amber-900 cursor-pointer flex items-center gap-2">
            <span>🎁 Perks &amp; Benefits</span>
            <span className="text-[9px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-bold">CTC-ONLY · NOT PAID IN PAYROLL</span>
          </summary>

          <div className="mt-3 space-y-3">
            {/* Explainer banner */}
            <div className="bg-white border border-amber-200 rounded-md p-2.5 text-[11px] text-slate-700" data-testid="ctc-perks-banner">
              <p className="font-semibold text-amber-900 mb-1">How perks work in the gross-based engine</p>
              <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                <li>Perks added here roll up into <b>Final CTC</b> on the offer letter / CTC document — they are <b>NOT</b> processed in the monthly payroll run.</li>
                <li>If you actually want to pay a perk to an employee, add it via <b>Payroll → Monthly Inputs</b> for the specific period.</li>
                <li>Common examples: Bonus, Gratuity, Insurance, Food Allowance, LTA, Telephone reimbursement, etc.</li>
              </ul>
            </div>

            {/* Quick-add chips — one click adds a pre-filled row */}
            <div>
              <p className="text-[10px] text-slate-500 mb-1.5 uppercase tracking-wide">Quick add</p>
              <div className="flex flex-wrap gap-1.5">
                {PRESET_PERKS.map(({ code, name, color }) => (
                  <button key={code}
                    data-testid={`ctc-perk-quickadd-${code.toLowerCase()}`}
                    onClick={() => {
                      // Idempotent — don't add a duplicate if already present.
                      if ((draft.custom_perks || []).some(p => (p.code || '').toUpperCase() === code)) return;
                      setDraft(d => ({
                        ...d,
                        custom_perks: [
                          ...(d.custom_perks || []),
                          { code, name, monthly_amount: 0, type: 'earning', payroll_processable: false },
                        ],
                      }));
                    }}
                    className={`text-[10px] px-2 py-1 rounded-full border bg-${color}-50 text-${color}-700 border-${color}-200 hover:bg-${color}-100 font-medium flex items-center gap-1`}>
                    <Plus className="w-2.5 h-2.5" /> {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Perks list — each row has explicit ₹/mo input + annual derived value */}
            <div className="bg-white rounded-md border border-slate-200 p-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-slate-700">Perks list</span>
                <button onClick={() => setDraft(d => ({ ...d, custom_perks: [...(d.custom_perks || []), { code: '', name: '', monthly_amount: 0, type: 'earning', payroll_processable: false }] }))}
                  className="flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded text-[10px] font-medium"
                  data-testid="ctc-custom-perk-add">
                  <Plus className="w-3 h-3" /> Add Blank Row
                </button>
              </div>
              {(draft.custom_perks || []).length === 0 ? (
                <p className="text-[11px] text-slate-400 px-2 py-3 text-center" data-testid="ctc-perks-empty">
                  No perks added. Use the quick-add chips above or "Add Blank Row" for company-specific perks.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {/* Column headers */}
                  <div className="grid grid-cols-12 gap-1 text-[9px] uppercase tracking-wide text-slate-500 px-1 pb-1 border-b border-slate-100">
                    <div className="col-span-2">Code</div>
                    <div className="col-span-4">Perk Name</div>
                    <div className="col-span-2 text-right">₹/Month</div>
                    <div className="col-span-2 text-right">₹/Year</div>
                    <div className="col-span-1 text-center">CTC-only</div>
                    <div className="col-span-1"></div>
                  </div>
                  {draft.custom_perks.map((p, i) => {
                    const monthly = Number(p.monthly_amount) || 0;
                    const annual = monthly * 12;
                    return (
                      <div key={i} className="grid grid-cols-12 gap-1 items-center bg-amber-50/50 rounded p-1" data-testid={`ctc-custom-perk-${i}`}>
                        <input value={p.code} placeholder="CODE" className="col-span-2 border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                          onChange={e => setPerk(i, { code: e.target.value.toUpperCase() })} />
                        <input value={p.name} placeholder="Perk name" className="col-span-4 border border-slate-300 rounded px-2 py-1 text-xs"
                          onChange={e => setPerk(i, { name: e.target.value })} />
                        <input type="number" value={p.monthly_amount} placeholder="0" className="col-span-2 border border-slate-300 rounded px-2 py-1 text-xs text-right font-mono"
                          onChange={e => setPerk(i, { monthly_amount: parseFloat(e.target.value) || 0 })} />
                        <div className="col-span-2 text-right text-xs font-mono text-slate-600">
                          ₹ {annual.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                        <div className="col-span-1 text-center">
                          <span className="text-[9px] bg-amber-100 text-amber-800 border border-amber-300 px-1 py-0.5 rounded font-bold">CTC</span>
                        </div>
                        <button onClick={() => setDraft(d => ({ ...d, custom_perks: d.custom_perks.filter((_, j) => j !== i) }))}
                          className="col-span-1 flex justify-center text-rose-500 hover:text-rose-700"
                          data-testid={`ctc-perk-remove-${i}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  {/* Total perks row */}
                  <div className="grid grid-cols-12 gap-1 items-center bg-amber-100 rounded p-1.5 mt-2 font-bold text-xs">
                    <div className="col-span-6 text-amber-900">Total Perks (added to Final CTC)</div>
                    <div className="col-span-2 text-right font-mono text-amber-900">
                      ₹ {((draft.custom_perks || []).reduce((s, p) => s + (Number(p.monthly_amount) || 0), 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}/mo
                    </div>
                    <div className="col-span-2 text-right font-mono text-amber-900" data-testid="ctc-perks-total-annual">
                      ₹ {preview.perks_annual.toLocaleString('en-IN', { maximumFractionDigits: 0 })}/yr
                    </div>
                    <div className="col-span-2"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </details>

        {/* Statutory Benefits Configuration (Group-wise Gratuity, Bonus & Leave Encashment) */}
        <details className="bg-purple-50/90 rounded-lg p-3 border border-purple-200" open>
          <summary className="text-xs font-semibold text-purple-900 cursor-pointer flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-purple-700" />
            <span>Statutory Benefits Configuration (Group-wise)</span>
            <span className="text-[9px] bg-purple-200 text-purple-900 px-1.5 py-0.5 rounded font-bold">GRATUITY · BONUS · LEAVE ENCASHMENT</span>
          </summary>

          <div className="mt-3 space-y-4 text-xs">
            <div className="bg-white border border-purple-200 rounded-md p-2.5 text-[11px] text-slate-700">
              <p className="font-semibold text-purple-900 mb-1">Group-Level Statutory Benefit Policy</p>
              <p className="text-slate-600">
                Configure eligibility, calculation frequency, calculation mode, and <strong>Payment Responsibility</strong> (Company Pays vs Client Company Pays) for employees in this group.
                Employee-level overrides in Employee Statutory Info take highest precedence.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {['gratuity', 'bonus', 'leave_encashment'].map(bKey => {
                const bLabel = bKey === 'gratuity' ? 'Gratuity' : (bKey === 'bonus' ? 'Statutory Bonus' : 'Leave Encashment');
                const bCfg = (draft.statutory_benefits && draft.statutory_benefits[bKey]) || {
                  is_eligible: false,
                  frequency: bKey === 'gratuity' ? 'monthly' : 'annual',
                  amount_mode: 'auto_calculate',
                  payment_responsibility: 'COMPANY_PAYS',
                  client_company_id: null,
                  period_lock: false,
                  effective_from: '',
                  effective_to: '',
                };

                return (
                  <div key={bKey} className={`bg-white rounded-lg border p-3 space-y-2 shadow-sm ${bCfg.is_eligible ? 'border-purple-300 ring-1 ring-purple-200' : 'border-slate-200 opacity-80'}`}>
                    <div className="flex items-center justify-between border-b pb-2">
                      <span className="font-semibold text-slate-800 text-xs flex items-center gap-1.5">
                        {bKey === 'gratuity' ? '📜' : bKey === 'bonus' ? '🎁' : '🏖️'} {bLabel}
                      </span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!bCfg.is_eligible}
                          onChange={e => setStatutoryBenefit(bKey, { is_eligible: e.target.checked })}
                          data-testid={`stat-eligible-${bKey}`}
                          className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className={`text-[10px] font-bold ${bCfg.is_eligible ? 'text-purple-700' : 'text-slate-400'}`}>
                          {bCfg.is_eligible ? 'Eligible' : 'Off'}
                        </span>
                      </label>
                    </div>

                    {bCfg.is_eligible ? (
                      <div className="space-y-2 pt-1">
                        <div>
                          <label className="text-[10px] font-medium text-slate-600 block mb-0.5">Frequency</label>
                          <select
                            value={bCfg.frequency || 'annual'}
                            onChange={e => setStatutoryBenefit(bKey, { frequency: e.target.value })}
                            data-testid={`stat-freq-${bKey}`}
                            className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                          >
                            <option value="monthly">Monthly Provision / Payment</option>
                            <option value="annual">Annual Provision / Payment</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-[10px] font-medium text-slate-600 block mb-0.5">Amount Mode</label>
                          <select
                            value={bCfg.amount_mode || 'auto_calculate'}
                            onChange={e => setStatutoryBenefit(bKey, { amount_mode: e.target.value })}
                            data-testid={`stat-mode-${bKey}`}
                            className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                          >
                            <option value="auto_calculate">Auto Calculate (Rule/Formula)</option>
                            <option value="ctc_upload">CTC Upload / Fixed</option>
                            <option value="manual">Manual Entry / Override</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-[10px] font-medium text-slate-600 block mb-0.5">Payment Responsibility</label>
                          <select
                            value={bCfg.payment_responsibility || 'COMPANY_PAYS'}
                            onChange={e => setStatutoryBenefit(bKey, { payment_responsibility: e.target.value })}
                            data-testid={`stat-resp-${bKey}`}
                            className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white font-medium text-purple-900"
                          >
                            <option value="COMPANY_PAYS">Company Pays (Direct)</option>
                            <option value="CLIENT_COMPANY_PAYS">Client Company Pays (Billable/Reimbursable)</option>
                          </select>
                        </div>

                        {bCfg.payment_responsibility === 'CLIENT_COMPANY_PAYS' && (
                          <div>
                            <label className="text-[10px] font-medium text-purple-700 block mb-0.5">Client Company *</label>
                            <select
                              value={bCfg.client_company_id || ''}
                              onChange={e => setStatutoryBenefit(bKey, { client_company_id: e.target.value || null })}
                              data-testid={`stat-client-${bKey}`}
                              className="w-full border border-purple-300 rounded px-2 py-1 text-xs bg-purple-50 font-medium text-purple-900"
                            >
                              <option value="">-- Select Client Company --</option>
                              {clientCompanies.map(c => (
                                <option key={c.id || c._id} value={c.id || c._id}>
                                  {c.name || c.company_name} ({c.code || 'CO'})
                                </option>
                              ))}
                            </select>
                            {!bCfg.client_company_id && (
                              <span className="text-[9px] text-rose-600 font-medium block mt-0.5">Client Company selection is required</span>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-slate-600 block mb-0.5">Effective From</label>
                            <input
                              type="date"
                              value={bCfg.effective_from || ''}
                              onChange={e => setStatutoryBenefit(bKey, { effective_from: e.target.value })}
                              data-testid={`stat-from-${bKey}`}
                              className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-slate-600 block mb-0.5">Effective To</label>
                            <input
                              type="date"
                              value={bCfg.effective_to || ''}
                              onChange={e => setStatutoryBenefit(bKey, { effective_to: e.target.value })}
                              data-testid={`stat-to-${bKey}`}
                              className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                            />
                          </div>
                        </div>

                        <div className="pt-1 flex items-center justify-between border-t border-slate-100">
                          <label className="flex items-center gap-1.5 cursor-pointer text-[10px] text-slate-600">
                            <input
                              type="checkbox"
                              checked={!!bCfg.period_lock}
                              onChange={e => setStatutoryBenefit(bKey, { period_lock: e.target.checked })}
                              data-testid={`stat-lock-${bKey}`}
                              className="rounded border-slate-300 text-purple-600"
                            />
                            <span>Period Lock</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[10px] text-slate-400 italic py-2 text-center">
                        Benefit disabled for this group. Check "Eligible" above to configure rules.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </details>

        <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-1">
          <button data-testid="ctc-create-btn" onClick={submit} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {initial ? 'Update Group' : 'Create Group'}
          </button>
          <button onClick={onCancel} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Stage 73.2 — Preset perk catalog used by the quick-add chips in the
// Perks section. Each click pushes a row into draft.custom_perks pre-filled
// with code + name, leaving the ₹/mo amount for the admin to type. These
// perks are CTC-document only — they do NOT feed the monthly payroll run.
const PRESET_PERKS = [
  { code: 'BONUS',     name: 'Annual Bonus',           color: 'amber'   },
  { code: 'GRATUITY',  name: 'Gratuity',               color: 'green'   },
  { code: 'INSURANCE', name: 'Health Insurance',       color: 'red'     },
  { code: 'LTA',       name: 'LTA',                    color: 'teal'    },
  { code: 'FOOD',      name: 'Food / Meal Allowance',  color: 'rose'    },
  { code: 'TEL',       name: 'Telephone Reimb.',       color: 'blue'    },
  { code: 'MOBILE',    name: 'Mobile Reimb.',          color: 'sky'     },
  { code: 'NET',       name: 'Internet Reimb.',        color: 'cyan'    },
  { code: 'CONV_PERK', name: 'Conveyance (perk)',      color: 'indigo'  },
  { code: 'DRIVER',    name: 'Driver Salary',          color: 'purple'  },
  { code: 'BOOKS',     name: 'Books / Periodicals',    color: 'pink'    },
];

// ------- sub-components (memoised) -------

const HeadRow = React.memo(function HeadRow({ h, idx, computed, headOptions, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-1 items-center bg-slate-50 rounded-md p-1.5"
      data-testid={`ctc-head-row-${idx}`}>

      <div className="col-span-3">
        <select value={h.code || ''}
          data-testid={`ctc-head-select-${idx}`}
          onChange={e => {
            const code = e.target.value;
            const sel = headOptions.find(m => m.code === code);
            onChange({
              code,
              name: sel ? sel.name : h.name,
              type: sel ? sel.type : h.type,
            });
          }}
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono bg-white">
          <option value="">-- Select --</option>
          {headOptions.map(m => (
            <option key={m.code} value={m.code}>{`${m.code} — ${m.name} (${m.type})`}</option>
          ))}
        </select>
      </div>

      <div className="col-span-1 text-center">
        <input type="checkbox" checked={h.ctc_component !== false}
          onChange={e => onChange({ ctc_component: e.target.checked })}
          data-testid={`ctc-head-inctc-${idx}`}
          className="rounded border-slate-300 text-blue-600" />
      </div>

      <div className="col-span-2">
        <select value={h.calc} onChange={e => onChange({ calc: e.target.value })}
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
          data-testid={`ctc-head-calc-${idx}`}>
          <option value="fixed">Fixed</option>
          <option value="percentage">% of</option>
          <option value="balancing">Balancing (auto)</option>
        </select>
      </div>

      <div className="col-span-2">
        {h.calc === 'percentage' ? (
          <select value={h.pct_of || ''}
            onChange={e => onChange({ pct_of: e.target.value.toUpperCase() })}
            className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono bg-white"
            data-testid={`ctc-head-pctof-${idx}`}>
            <option value="">— pick reference —</option>
            <optgroup label="Constants">
              {CTC_REF_CONSTANTS.map(r => (
                <option key={r.value} value={r.value}>{`${r.value} — ${r.label.split(' — ')[1] || r.label}`}</option>
              ))}
            </optgroup>
            <optgroup label="Other Salary Heads">
              {headOptions.filter(m => m.code && m.code !== h.code)
                .map(m => (
                  <option key={m.code} value={m.code}>{`${m.code} — ${m.name}`}</option>
                ))}
            </optgroup>
          </select>
        ) : h.calc === 'balancing' ? (
          <span className="text-[10px] text-slate-500">fills CTC gap</span>
        ) : (
          <span className="text-[10px] text-slate-400">fixed ₹</span>
        )}
      </div>

      <div className="col-span-1">
        {h.calc === 'percentage' ? (
          <input type="text" inputMode="decimal" value={h.pct_val ?? ''}
            onChange={e => onChange({ pct_val: e.target.value.replace(/[^0-9.]/g, '') })}
            placeholder="%"
            className="w-full border border-slate-300 rounded px-2 py-1 text-xs text-right font-mono"
            data-testid={`ctc-head-pctval-${idx}`} />
        ) : h.calc === 'fixed' ? (
          <input type="text" inputMode="decimal" value={h.value ?? ''}
            onChange={e => onChange({ value: e.target.value.replace(/[^0-9.]/g, '') })}
            placeholder="₹/yr"
            className="w-full border border-slate-300 rounded px-2 py-1 text-xs text-right font-mono"
            data-testid={`ctc-head-value-${idx}`} />
        ) : (
          <span className="text-[10px] text-slate-400">—</span>
        )}
      </div>

      <div className="col-span-2 text-right font-mono text-xs text-blue-700">
        {computed?._annual != null
          ? `₹ ${Number(computed._annual).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
          : '—'}
        {computed?._error && <div className="text-[10px] text-rose-600">{computed._error}</div>}
        {computed?._cap_note && (
          <div className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 mt-0.5"
               data-testid={`ctc-head-cap-${idx}`}>
            ⚠ {computed._cap_note}
          </div>
        )}
      </div>

      <div className="col-span-1 text-right">
        <button onClick={onRemove} className="text-red-400 hover:text-red-600"
          data-testid={`ctc-head-remove-${idx}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
});

function LabelledInput({ label, value, onChange, placeholder, type = 'text', mono = false, testid, disabled = false }) {
  return (
    <div>
      <label className="block text-[10px] font-medium text-slate-600 mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm ${mono ? 'font-mono' : ''} ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
      />
    </div>
  );
}

function PreviewStat({ label, value, color = 'slate', testid }) {
  const cls = {
    blue:    'bg-blue-100 text-blue-800',
    amber:   'bg-amber-100 text-amber-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    rose:    'bg-rose-100 text-rose-800',
    slate:   'bg-slate-100 text-slate-800',
    indigo:  'bg-indigo-100 text-indigo-800',
  }[color] || 'bg-slate-100 text-slate-800';
  return (
    <div className={`${cls} rounded-md p-2`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-sm font-bold font-mono">
        ₹ {Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}
